import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { Star, StickyNote, CalendarCheck, MapPin, Play, FileText, ImageIcon, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const ALLOWED = ["super_admin", "admin_lcs", "recruteur_lcs", "assistant_recruteur", "employeur", "manager_employeur"];
const KIND_ICONS = { video: Play, image: ImageIcon, document: FileText };
const PROVENANCE_LABELS = { personnelle: "Réalisation personnelle", collectif: "Projet collectif", simulation: "Simulation" };

export default function CandidatePortfolio() {
  const { candidateId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [viewer, setViewer] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const allowed = user && ALLOWED.includes(user.role);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
    else if (!authLoading && user && !ALLOWED.includes(user.role)) navigate("/dashboard", { replace: true });
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    const [p, n] = await Promise.all([
      api.get(`/showcase/profile/${candidateId}`),
      api.get(`/showcase/notes/${candidateId}`),
    ]);
    setProfile(p.data);
    setNotes(n.data);
  }, [candidateId]);

  useEffect(() => {
    if (allowed) load().catch((e) => setError(formatApiError(e)));
  }, [allowed, load]);

  if (!user || !allowed) return null;

  const toggleFavorite = async () => {
    try {
      const res = await api.post(`/showcase/favorites/${candidateId}`);
      setProfile({ ...profile, is_favorite: res.data.favorite });
    } catch (err) { setError(formatApiError(err)); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setError("");
    try {
      await api.post(`/showcase/notes/${candidateId}`, { note: noteText });
      setNoteText("");
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const requestInterview = async () => {
    setError(""); setNotice("");
    try {
      const res = await api.post(`/showcase/profile/${candidateId}/interview-request`);
      setNotice(res.data.status === "deja_envoyee"
        ? "Une demande d'entrevue est déjà en attente pour ce candidat."
        : "Demande d'entrevue envoyée au candidat.");
    } catch (err) { setError(formatApiError(err)); }
  };

  const cv = profile?.cv;

  return (
    <div data-testid="candidate-portfolio-page" className="min-h-screen bg-slate-50">
      <PublicNav />
      <div className="bg-brand-navy">
        <div className="max-w-5xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-white">{profile?.candidate?.name || "Candidat"}</h1>
            <p className="text-white/70 text-sm mt-1 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              {cv ? `${cv.profession || "—"} · ${[cv.city, cv.country].filter(Boolean).join(", ") || "—"}` : "Profil"}
            </p>
          </div>
          <div className="flex gap-2">
            <button data-testid="favorite-toggle" onClick={toggleFavorite}
              className={`flex items-center gap-2 text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors ${
                profile?.is_favorite ? "bg-amber-400 text-brand-navy" : "bg-white/10 hover:bg-white/20 text-white"}`}>
              <Star className={`w-4 h-4 ${profile?.is_favorite ? "fill-brand-navy" : ""}`} />
              {profile?.is_favorite ? "Favori" : "Ajouter aux favoris"}
            </button>
            <button data-testid="interview-request" onClick={requestInterview}
              className="flex items-center gap-2 bg-brand-turquoise hover:bg-brand-turquoise/90 text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors">
              <CalendarCheck className="w-4 h-4" /> Demander une entrevue
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {error && <div data-testid="profile-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
        {notice && <div data-testid="profile-notice" className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{notice}</div>}

        {cv && (
          <div data-testid="profile-cv" className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="font-semibold text-brand-navy mb-2">CV et expérience</h2>
            <p className="text-sm text-slate-600">{cv.summary || "Résumé non renseigné."}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(cv.skills || []).map((s) => (
                <span key={s} className="text-xs bg-brand-sky text-brand-navy rounded-full px-2.5 py-0.5">{s}</span>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3">
              {cv.experience_years} an(s) d'expérience · Disponibilité : {cv.availability || "à confirmer"} · Mobilité : {cv.mobility || "—"}
            </p>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-semibold text-brand-navy mb-1">Réalisations partagées ({profile?.items?.length || 0})</h2>
          <p className="text-xs text-slate-400 mb-4">
            Contenus déclarés par le candidat. Une vidéo illustre une réalisation, elle ne certifie pas à elle seule une qualification.
          </p>
          {profile && profile.items.length === 0 && (
            <p data-testid="profile-media-empty" className="text-sm text-slate-400 text-center py-8">
              Ce candidat n'a pas encore partagé de réalisation. Ses critères professionnels (CV, expérience) restent pleinement évaluables.
            </p>
          )}
          <div data-testid="profile-media-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(profile?.items || []).map((m) => {
              const Icon = KIND_ICONS[m.kind] || FileText;
              return (
                <div key={m.media_id} data-testid={`profile-media-${m.media_id}`}
                  className="border border-slate-100 rounded-lg overflow-hidden cursor-pointer hover:border-brand-turquoise transition-colors"
                  onClick={() => setViewer(m)}>
                  <div className="h-36 bg-slate-100 flex items-center justify-center">
                    {m.kind === "image" && m.has_thumbnail ? (
                      <img src={`${API}/api/showcase/media/${m.media_id}/thumb`} alt={m.title} className="h-full w-full object-cover" />
                    ) : (
                      <Icon className="w-9 h-9 text-slate-300" />
                    )}
                  </div>
                  <div className="p-3.5">
                    <p className="font-semibold text-brand-navy text-sm">{m.title || m.original_filename}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{m.metier || "—"} · {PROVENANCE_LABELS[m.provenance] || m.provenance}</p>
                    {m.skills.length > 0 && (
                      <p className="text-xs text-brand-turquoise mt-1">{m.skills.join(", ")}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div data-testid="profile-notes" className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-semibold text-brand-navy mb-1 flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-brand-turquoise" /> Notes privées de votre organisation
          </h2>
          <p className="text-xs text-slate-400 mb-3">Visibles uniquement par votre organisation — jamais par le candidat.</p>
          <div className="flex gap-2 mb-3">
            <input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)}
              placeholder="Ajouter une note privée…" className="flex-1 text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
            <button data-testid="note-submit" onClick={addNote}
              className="bg-brand-navy text-white text-sm font-semibold rounded-lg px-4 py-2.5">Ajouter</button>
          </div>
          <div className="space-y-2">
            {notes.map((n) => (
              <div key={n.note_id} data-testid={`note-${n.note_id}`} className="text-sm bg-slate-50 rounded-lg px-3.5 py-2.5">
                <p className="text-slate-700">{n.note}</p>
                <p className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("fr-CA")}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {viewer && (
        <div data-testid="media-viewer" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setViewer(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-brand-navy">{viewer.title || viewer.original_filename}</h3>
              <button data-testid="viewer-close" onClick={() => setViewer(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            {viewer.kind === "video" && (
              <video data-testid="viewer-video" controls autoPlay src={`${API}/api/showcase/media/${viewer.media_id}/content`}
                className="w-full rounded-lg bg-black max-h-[60vh]" />
            )}
            {viewer.kind === "image" && (
              <img data-testid="viewer-image" src={`${API}/api/showcase/media/${viewer.media_id}/content`}
                alt={viewer.title} className="w-full rounded-lg max-h-[60vh] object-contain bg-slate-50" />
            )}
            {viewer.kind === "document" && (
              <a data-testid="viewer-document" href={`${API}/api/showcase/media/${viewer.media_id}/content`}
                target="_blank" rel="noopener noreferrer"
                className="block text-center text-sm font-semibold text-brand-blue hover:text-brand-turquoise py-6">
                Ouvrir le document dans un nouvel onglet
              </a>
            )}
            {viewer.description && <p className="text-sm text-slate-600">{viewer.description}</p>}
            <p className="text-xs text-slate-400">
              {PROVENANCE_LABELS[viewer.provenance] || viewer.provenance} · Rôle déclaré : {viewer.role_in_project || "non précisé"} · {viewer.work_date || "date n/d"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
